import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiPost } from '@/api/client';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { FormActions, Textarea } from '@/app/components/ui/Field';
import { Modal } from '@/app/components/ui/Modal';
import { useSubmit } from '@/lib/useSubmit';

interface ImportRow {
  row: number;
  success: boolean;
  user_id?: string;
  error?: string;
}

interface ImportResult {
  imported: number;
  failed: number;
  rows: ImportRow[];
}

/**
 * Parses a CSV blob into `POST /users/import`'s row shape. Header row
 * optional: a first line that reads as `naam,email,afdeling,locatie` (any
 * case, nl/en) is treated as a header and skipped, anything else is data.
 * No dependency pulled in for this — the shape is four plain columns, a
 * hand comma-split with quoted-field support is enough and keeps the modal
 * self-contained.
 */
function parseCsv(
  text: string,
): { name?: string; email: string; department?: string; location?: string }[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');

  function splitLine(line: string): string[] {
    // Handles a quoted field containing a comma; good enough for a
    // name/email/department/location row, not a general CSV parser.
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    return cells;
  }

  const rows = lines.map(splitLine);
  const first = rows[0]?.map((cell) => cell.toLowerCase()) ?? [];
  const looksLikeHeader =
    first.includes('email') ||
    first.includes('e-mail') ||
    first.includes('naam') ||
    first.includes('name');
  const dataRows = looksLikeHeader ? rows.slice(1) : rows;

  return dataRows.map((cells) => {
    const row: { name?: string; email: string; department?: string; location?: string } = {
      email: cells[1] ?? '',
    };
    if (cells[0]) row.name = cells[0];
    if (cells[2]) row.department = cells[2];
    if (cells[3]) row.location = cells[3];
    return row;
  });
}

/**
 * The Medewerkers list's "CSV importeren" modal (PLAN-FASE2.md WP-01): plak
 * of plak-in CSV (naam, email, afdeling, locatie) en stuur naar
 * `POST /users/import`. Toont per rij succes/fout — nooit een silent
 * partial-fail: één foutieve rij (bv. een ongeldig e-mailadres) blokkeert de
 * andere rijen niet, en het resultaat laat precies zien welke rij faalde en
 * waarom.
 */
export function ImportUsersModal({
  open,
  onClose,
  companyId,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onImported: () => void;
}) {
  const { t } = useTranslation();
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  const form = useSubmit(async () => {
    const rows = parseCsv(csv);
    const response = (await apiPost('/users/import', {
      body: { company_id: companyId, rows },
    })) as ImportResult | undefined;
    setResult(response ?? null);
    if (response && response.imported > 0) {
      onImported();
    }
  });

  function reset() {
    setCsv('');
    setResult(null);
    form.clearError();
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleFile(file: File) {
    setCsv(await file.text());
  }

  return (
    <Modal open={open} onClose={handleClose} title={t('users.import.title')} className="max-w-lg">
      {result ? (
        <div role="status" className="flex flex-col gap-4">
          <p className="text-ink text-sm">
            {t('users.import.summary', { imported: result.imported, failed: result.failed })}
          </p>
          <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto text-[13px]">
            {result.rows.map((row) => (
              <li
                key={row.row}
                className={row.success ? 'text-ink' : 'text-danger'}
                role={row.success ? undefined : 'alert'}
              >
                {t('users.import.rowLabel', { row: row.row + 1 })}:{' '}
                {row.success
                  ? t('users.import.rowSuccess')
                  : (row.error ?? t('users.import.rowFailed'))}
              </li>
            ))}
          </ul>
          <FormActions>
            <Button onClick={handleClose}>{t('users.import.done')}</Button>
          </FormActions>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
        >
          <p className="text-ink text-sm">{t('users.import.instructions')}</p>
          <label className="flex flex-col gap-1.5">
            <span className="text-ink text-sm font-medium">{t('users.import.csvLabel')}</span>
            <Textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder={t('users.import.placeholder')}
              rows={8}
              required
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-ink text-sm font-medium">{t('users.import.uploadLabel')}</span>
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
              className="text-ink text-sm"
            />
          </label>
          <SubmitError error={form.error} />
          <FormActions>
            <Button type="button" variant="ghost" onClick={handleClose}>
              {t('users.import.cancel')}
            </Button>
            <Button type="submit" disabled={form.pending || csv.trim() === ''}>
              {t('users.import.submit')}
            </Button>
          </FormActions>
        </form>
      )}
    </Modal>
  );
}
